import { useEffect, useState } from 'react';
import clsx from 'clsx';
import ClickAwayListener from 'react-click-away-listener';
import { Button, Tooltip, Whisper } from 'rsuite';
import { Info } from 'lucide-react';
import {
  OCCUPATION_OPTIONS,
  OTHER_OCCUPATION_OPTION,
  OccupationOption,
  resolveOccupationOption,
} from '../../utils/occupationOptions';
import styles from './OccupationSelect.module.css';

type OccupationSelectProps = {
  value: string;
  error?: string;
  touched?: boolean;
  onChange: (occupation: string) => void;
  onTouched: (touched: boolean) => void;
  onBlur: () => void;
  variant?: 'auth' | 'light';
  label?: string;
  required?: boolean;
  labelClassName?: string;
  validClassName?: string;
  invalidClassName?: string;
  errorClassName?: string;
  mandatoryClassName?: string;
  infoTooltip?: string;
  infoIconClassName?: string;
};

export const OccupationSelect = ({
  value,
  error,
  touched,
  onChange,
  onTouched,
  onBlur,
  variant = 'light',
  label = 'Occupation',
  required = true,
  labelClassName,
  validClassName,
  invalidClassName,
  errorClassName,
  mandatoryClassName,
  infoTooltip,
  infoIconClassName,
}: OccupationSelectProps) => {
  const [occupationOption, setOccupationOption] = useState(() => resolveOccupationOption(value));
  const [isOpen, setIsOpen] = useState(false);
  const isOther = occupationOption === OTHER_OCCUPATION_OPTION;
  const isAuth = variant === 'auth';
  const hasError = Boolean(touched && error);
  const isSelectValid = Boolean(touched && !error && occupationOption && !isOther);
  const isSelectInvalid = Boolean(hasError && !isOther);
  const isOtherValid = Boolean(isOther && touched && !error);
  const isOtherInvalid = Boolean(isOther && hasError);
  const displayValue = occupationOption || 'Select your occupation';

  useEffect(() => {
    setOccupationOption((current) => {
      if (value) {
        return resolveOccupationOption(value);
      }

      // Keep "Other" selected while its custom field is empty.
      return current === OTHER_OCCUPATION_OPTION ? current : '';
    });
  }, [value]);

  const closeMenu = (shouldBlur = true) => {
    setIsOpen(false);
    if (shouldBlur) {
      onBlur();
    }
  };

  const handleOptionChange = (nextOption: string) => {
    if (nextOption === OTHER_OCCUPATION_OPTION) {
      setOccupationOption(OTHER_OCCUPATION_OPTION);
      onChange('');
      onTouched(false);
      closeMenu(false);
      return;
    }

    setOccupationOption(nextOption as OccupationOption | '');
    onChange(nextOption);
    onTouched(true);
    closeMenu(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onBlur]);

  return (
    <div className='inputWrapper' title='Select your current occupation or job title'>
      <div className='flex items-center justify-between'>
        <label className={clsx('label', labelClassName)}>
          {label}
          {required && <span className={mandatoryClassName}>*</span>}
        </label>
        {infoTooltip && (
          <Whisper
            trigger='hover'
            placement='top'
            controlId='occupation-info'
            speaker={<Tooltip>{infoTooltip}</Tooltip>}
          >
            <Button appearance='subtle' className={infoIconClassName}>
              <Info size={18} />
            </Button>
          </Whisper>
        )}
      </div>
      <ClickAwayListener onClickAway={() => isOpen && closeMenu()}>
        <div className={styles.root}>
          <button
            type='button'
            className={clsx(
              'inputField',
              styles.trigger,
              isAuth ? styles.triggerAuth : styles.triggerLight,
              isSelectInvalid && invalidClassName,
              isSelectValid && validClassName
            )}
            onClick={() => {
              if (isOpen) {
                closeMenu();
                return;
              }
              setIsOpen(true);
            }}
            aria-haspopup='listbox'
            aria-expanded={isOpen}
          >
            <span
              className={clsx(
                styles.value,
                !occupationOption && styles.placeholder,
                !occupationOption && isAuth && styles.placeholderAuth
              )}
            >
              {displayValue}
            </span>
            <span className={clsx(styles.chevron, isOpen && styles.chevronOpen)} aria-hidden />
          </button>
          {isOpen && (
            <div
              className={clsx(styles.menu, isAuth ? styles.menuAuth : styles.menuLight)}
              role='listbox'
            >
              {OCCUPATION_OPTIONS.map((option) => (
                <button
                  key={option}
                  type='button'
                  role='option'
                  aria-selected={occupationOption === option}
                  className={clsx(
                    styles.option,
                    isAuth ? styles.optionAuth : styles.optionLight,
                    occupationOption === option &&
                      (isAuth ? styles.optionAuthSelected : styles.optionLightSelected)
                  )}
                  onClick={() => handleOptionChange(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </ClickAwayListener>
      {isOther && (
        <div
          className={clsx(
            'inputField',
            styles.otherField,
            isOtherInvalid && invalidClassName,
            isOtherValid && validClassName
          )}
        >
          <input
            type='text'
            autoComplete='off'
            value={value}
            placeholder='Enter your occupation'
            onChange={(event) => onChange(event.target.value)}
            onBlur={onBlur}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
        </div>
      )}
      {hasError && (
        <div className={errorClassName}>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
